// pricing-calculator/utils/selection.ts
// Selection helpers: bundles, toggles, filtering by platform, price overrides.

import type {
    ID,
    Preset,
    Module,
    ModuleGroup,
    Platform,
  } from '@dopomogai/types';
  import { buildFeatureIndex, isFeatureCompatibleWithPlatform } from './compat';
  
  /**
   * Toggle an ID in a set (immutable).
   */
  export function toggleInSet<T>(set: Set<T>, id: T): Set<T> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }
  
  /**
   * Replace or merge selection with a preset's modules.
   * If merge=false (default), returns exactly preset.moduleIds. If merge=true, unions both.
   */
  export function applyPresetToSelection(
    selected: Set<ID>,
    preset?: Preset,
    merge = false
  ): Set<ID> {
    if (!preset) return new Set(selected);
    const fromPreset = new Set(preset.moduleIds || []);
    if (merge) {
      const next = new Set(selected);
      (preset.moduleIds || []).forEach((id: ID) => next.add(id));
      return next;
    }    
    return fromPreset;
  }
  
  /**
   * Returns a filtered selection that excludes modules incompatible with the platform.
   */
  export function filterSelectionForPlatform(
    selected: Set<ID>,
    groups: ModuleGroup[],
    platform?: Platform
  ): Set<ID> {
    if (!platform) return new Set(selected);
    const index = buildFeatureIndex(groups);
    const next = new Set<ID>();
    selected.forEach((id) => {
      const f = index[id];
      if (!f) return;
      if (isFeatureCompatibleWithPlatform(f, platform.id)) next.add(id);
    });
    return next;
  }
  
  /**
   * Given groups and a selection set, return the selected module objects in original spec order.
   */
  export function getSelectedModules(groups: ModuleGroup[], selected: Set<ID>): Module[] {
    const result: Module[] = [];
    for (const g of groups) {
      for (const f of g.features) {
        if (selected.has(f.id)) result.push(f);
      }
    }
    return result;
  }
  
  /**
   * Compute the subtotal for selected modules, applying a preset priceOverride if provided.
   * - If no preset or no override: normal sum.
   * - If override: sum(non-preset modules) + min(override, sum(preset-contained selected modules))
   */
  export function computeModuleSubtotalWithPreset(
    groups: ModuleGroup[],
    selected: Set<ID>,
    preset?: Preset
  ): number {
    const selectedModules = getSelectedModules(groups, selected);
    if (!preset || typeof preset.priceOverrideUSD !== 'number') {
      return selectedModules.reduce((sum, f) => sum + f.priceUSD, 0);
    }
  
    const inPreset = new Set(preset.moduleIds || []);
    let inSum = 0;
    let outSum = 0;
    selectedModules.forEach((f) => {
      if (inPreset.has(f.id)) inSum += f.priceUSD;
      else outSum += f.priceUSD;
    });
  
    return outSum + Math.min(preset.priceOverrideUSD, inSum);
  }
  
  /**
   * Remove a set of IDs from a selection (immutable).
   */
  export function deselectMany(selected: Set<ID>, ids: ID[]): Set<ID> {
    const next = new Set(selected);
    ids.forEach((id) => next.delete(id));
    return next;
  }
  
  /**
   * Add a set of IDs to a selection (immutable).
   */
  export function selectMany(selected: Set<ID>, ids: ID[]): Set<ID> {
    const next = new Set(selected);
    ids.forEach((id) => next.add(id));
    return next;
  }
  